/* *
 * PriceModal Live pricing from /api/pricing/catalog
 *
 * Usage:
 *   import { PriceModal } from './price-modal.js';
 *   PriceModal.open();
 *
 * Or declaratively on any element:
 *   <button data-open-price-modal>View Pricing</button> */

"use strict";

const MODAL_ID = "ad-price-modal";
const CACHE_KEY = "ascend_price_modal_v1";

function resolveApiBase() {
  if (window.PortEngine?.getApiUrl) return window.PortEngine.getApiUrl();
  const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return isLocal
    ? "http://localhost:5001/api"
    : "https://api.ascenddraft.com/api";
}

const CATEGORY_META = {
  academic: {
    label: "Academic Writing",
    emoji: "📝",
    note: "per-word pricing",
  },
  career: { label: "Career Writing", emoji: "💼", note: "flat-rate pricing" },
  design: {
    label: "Design Services",
    emoji: "🎨",
    note: "per-project pricing",
  },
  digital: {
    label: "Digital Services",
    emoji: "💻",
    note: "per-project pricing",
  },
  tutoring: { label: "Tutoring", emoji: "🎓", note: "per-session pricing" },
  automation: {
    label: "AI & Automation",
    emoji: "⚡",
    note: "per-task pricing",
  },
};

const CATEGORY_ORDER = [
  "academic",
  "career",
  "design",
  "digital",
  "tutoring",
  "automation",
];

// ── inline styles (self-contained) ───────────────────────────────────────────
const STYLES = `
/* ── Price Modal theme-aware via CSS custom properties ───────────────────── */
:root {
  --pm-bg:          #ffffff;
  --pm-border:      rgba(0,0,0,0.1);
  --pm-text:        #1e293b;
  --pm-text-muted:  #64748b;
  --pm-text-subtle: #94a3b8;
  --pm-divider:     rgba(0,0,0,0.08);
  --pm-row-divider: rgba(0,0,0,0.05);
  --pm-disclaimer-bg: rgba(0,0,0,0.04);
  --pm-close-border: rgba(0,0,0,0.15);
  --pm-close-hover:  rgba(0,0,0,0.08);
  --pm-ghost-border: rgba(0,0,0,0.15);
  --pm-ghost-hover:  rgba(0,0,0,0.06);
  --pm-cat-name:    #2563eb;
  --pm-price-color: #059669;
  --pm-disclaimer-highlight: #92400e;
  --pm-disclaimer-strong: #78350f;
}
[data-theme="dark"], .theme-dark {
  --pm-bg:          #0F172A;
  --pm-border:      rgba(255,255,255,0.1);
  --pm-text:        #E2E8F0;
  --pm-text-muted:  #94A3B8;
  --pm-text-subtle: #64748B;
  --pm-divider:     rgba(255,255,255,0.08);
  --pm-row-divider: rgba(255,255,255,0.04);
  --pm-disclaimer-bg: rgba(255,255,255,0.04);
  --pm-close-border: rgba(255,255,255,0.15);
  --pm-close-hover:  rgba(255,255,255,0.1);
  --pm-ghost-border: rgba(255,255,255,0.12);
  --pm-ghost-hover:  rgba(255,255,255,0.07);
  --pm-cat-name:    #60A5FA;
  --pm-price-color: #34D399;
  --pm-disclaimer-highlight: #F59E0B;
  --pm-disclaimer-strong: #FCD34D;
}
#ad-price-modal-backdrop {
  position: fixed; inset: 0; z-index: 9998;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: pmFadeIn 0.18s ease;
}
#ad-price-modal {
  background: var(--pm-bg);
  border: 1px solid var(--pm-border);
  border-radius: 16px;
  width: 100%; max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  display: flex; flex-direction: column;
  animation: pmSlideUp 0.22s ease;
  color: var(--pm-text);
  font-family: inherit;
}
.pm-header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid var(--pm-divider);
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
}
.pm-title { font-size: 1.25rem; font-weight: 700; color: var(--pm-text); margin: 0 0 4px; }
.pm-subtitle { font-size: 0.8rem; color: var(--pm-text-muted); margin: 0; line-height: 1.5; }
.pm-close {
  flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%;
  border: 1px solid var(--pm-close-border); background: transparent;
  color: var(--pm-text-muted); cursor: pointer; font-size: 18px; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.pm-close:hover { background: var(--pm-close-hover); color: var(--pm-text); }
.pm-body { overflow-y: auto; padding: 20px 28px; flex: 1; }
.pm-loading { text-align: center; padding: 40px 0; color: var(--pm-text-subtle); font-size: 0.95rem; }
.pm-error { text-align: center; padding: 32px 0; color: #ef4444; font-size: 0.9rem; }
.pm-category { margin-bottom: 24px; }
.pm-cat-header {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 10px; padding-bottom: 8px;
  border-bottom: 1px solid var(--pm-divider);
}
.pm-cat-emoji { font-size: 1rem; }
.pm-cat-name { font-size: 0.85rem; font-weight: 700; color: var(--pm-cat-name); text-transform: uppercase; letter-spacing: 0.06em; }
.pm-cat-note { font-size: 0.72rem; color: var(--pm-text-subtle); margin-left: auto; font-style: italic; }
.pm-service-row {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 12px; padding: 7px 0;
  border-bottom: 1px solid var(--pm-row-divider);
}
.pm-service-row:last-child { border-bottom: none; }
.pm-svc-name { font-size: 0.875rem; color: var(--pm-text); }
.pm-svc-price { font-size: 0.875rem; font-weight: 600; color: var(--pm-price-color); white-space: nowrap; flex-shrink: 0; }
.pm-footer {
  padding: 16px 28px 20px;
  border-top: 1px solid var(--pm-divider);
  display: flex; flex-direction: column; gap: 12px;
}
.pm-disclaimer {
  font-size: 0.75rem; color: var(--pm-text-muted); line-height: 1.55;
  background: var(--pm-disclaimer-bg); border-radius: 8px; padding: 10px 14px;
  border-left: 3px solid var(--pm-disclaimer-highlight);
}
.pm-disclaimer strong { color: var(--pm-disclaimer-strong); }
.pm-actions { display: flex; gap: 10px; justify-content: flex-end; }
.pm-btn-ghost {
  padding: 10px 18px; border-radius: 8px;
  background: transparent; color: var(--pm-text-muted);
  font-size: 0.875rem; font-weight: 500;
  border: 1px solid var(--pm-ghost-border); cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.pm-btn-ghost:hover { background: var(--pm-ghost-hover); color: var(--pm-text); }
@keyframes pmFadeIn { from { opacity:0 } to { opacity:1 } }
@keyframes pmSlideUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
@media (max-width: 520px) {
  .pm-header, .pm-body, .pm-footer { padding-left: 18px; padding-right: 18px; }
  .pm-actions { flex-direction: column-reverse; }
  .pm-btn-ghost { text-align: center; justify-content: center; }
}
`;

function injectStyles() {
  if (document.getElementById("ad-price-modal-styles")) return;
  const s = document.createElement("style");
  s.id = "ad-price-modal-styles";
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function formatPrice(svc) {
  if (!svc.basePrice && svc.basePrice !== 0) return "Available on request";
  const amt =
    Number(svc.basePrice) % 1 === 0
      ? String(svc.basePrice)
      : Number(svc.basePrice).toFixed(2);
  const unit = (svc.unit || "").replace("/100 words", "/ 100 words").trim();
  return unit ? `$${amt} ${unit}` : `$${amt}`;
}

async function fetchPrices() {
  const cached = (() => {
    try {
      const r = sessionStorage.getItem(CACHE_KEY);
      if (!r) return null;
      const { ts, data } = JSON.parse(r);
      if (Date.now() - ts < 5 * 60 * 1000) return data;
    } catch {
      /* ignore */
    }
    return null;
  })();
  if (cached) return cached;

  const res = await fetch(`${resolveApiBase()}/pricing/catalog`, {
    signal: AbortSignal.timeout(8000),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error("API returned success:false");
  const cats = json.data?.categories || [];
  const data = { categories: cats };
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore */
  }
  return data;
}

function buildContent(categories) {
  const byKey = Object.fromEntries(categories.map((c) => [c.key, c]));
  const ordered = CATEGORY_ORDER.map((k) => byKey[k])
    .filter(Boolean)
    .concat(categories.filter((c) => !CATEGORY_ORDER.includes(c.key)));

  if (!ordered.length)
    return `<p class="pm-error">No pricing data available right now.</p>`;

  return ordered
    .map((cat) => {
      const meta = CATEGORY_META[cat.key] || {
        label: cat.label || cat.key,
        emoji: "📋",
        note: "",
      };
      const services = (cat.services || []).filter(
        (s) => s.websiteVisible !== false,
      );
      if (!services.length) return "";

      const rows = services
        .map((svc) => {
          const priceStr = svc.priceRange || formatPrice(svc);
          return `<div class="pm-service-row">
        <span class="pm-svc-name">${svc.name || svc.code}</span>
        <span class="pm-svc-price">${priceStr}</span>
      </div>`;
        })
        .join("");

      return `<div class="pm-category">
      <div class="pm-cat-header">
        <span class="pm-cat-emoji">${meta.emoji}</span>
        <span class="pm-cat-name">${meta.label}</span>
        ${meta.note ? `<span class="pm-cat-note">${meta.note}</span>` : ""}
      </div>
      ${rows}
    </div>`;
    })
    .join("");
}

function buildModal() {
  const backdrop = document.createElement("div");
  backdrop.id = "ad-price-modal-backdrop";
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-modal", "true");
  backdrop.setAttribute("aria-label", "Current pricing");

  backdrop.innerHTML = `
    <div id="${MODAL_ID}">
      <div class="pm-header">
        <div>
          <h2 class="pm-title">Current Pricing</h2>
          <p class="pm-subtitle">Live prices from our service catalog &mdash; updated in real time.</p>
        </div>
        <button class="pm-close" aria-label="Close pricing modal">&times;</button>
      </div>
      <div class="pm-body">
        <div class="pm-loading">Loading prices&hellip;</div>
      </div>
      <div class="pm-footer">
        <div class="pm-disclaimer">
          <strong>Note:</strong> Prices shown are <strong>base rates</strong>.
          For <strong>Academic</strong> services, the final cost scales with your word count, academic level (Undergraduate / Master's / PhD), and selected tier (Proofreading, Draft Refining, or Model Paper).
          For all other services, the final amount is confirmed when you add items to your basket, since complexity, urgency, and any add-ons may increase the total.
        </div>
        <div class="pm-actions">
          <button class="pm-btn-ghost pm-quote-btn">🧮 Get a Quote</button>
          <button class="pm-btn-ghost pm-close-btn">Close</button>
        </div>
      </div>
    </div>`;

  return backdrop;
}

// ── Public API ───────────────────────────────────────────────────────────────

export const PriceModal = {
  _backdrop: null,

  open() {
    if (document.getElementById("ad-price-modal-backdrop")) return;
    injectStyles();

    const backdrop = buildModal();
    this._backdrop = backdrop;
    document.body.appendChild(backdrop);
    document.body.style.overflow = "hidden";

    // Bind close handlers
    const close = () => this.close();
    backdrop.querySelector(".pm-close").addEventListener("click", close);
    backdrop.querySelector(".pm-close-btn").addEventListener("click", close);
    const quoteBtn = backdrop.querySelector(".pm-quote-btn");
    if (quoteBtn)
      quoteBtn.addEventListener("click", () => {
        close();
        if (window.QuoteCalculator) window.QuoteCalculator.open();
      });
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
    document.addEventListener(
      "keydown",
      (this._keyHandler = (e) => {
        if (e.key === "Escape") close();
      }),
    );

    // Fetch and render prices
    fetchPrices()
      .then(({ categories }) => {
        const body = backdrop.querySelector(".pm-body");
        if (body) body.innerHTML = buildContent(categories);
      })
      .catch((err) => {
        const body = backdrop.querySelector(".pm-body");
        if (body)
          body.innerHTML = `<p class="pm-error">Could not load prices. Please try again later.</p>`;
        console.warn("[PriceModal] fetch failed:", err.message);
      });
  },

  close() {
    const el = document.getElementById("ad-price-modal-backdrop");
    if (el) el.remove();
    document.body.style.overflow = "";
    if (this._keyHandler) {
      document.removeEventListener("keydown", this._keyHandler);
      this._keyHandler = null;
    }
  },
};

// ── Auto-wire [data-open-price-modal] triggers ───────────────────────────────
function wireTriggers() {
  document.querySelectorAll("[data-open-price-modal]").forEach((el) => {
    if (el.dataset.pmWired) return;
    el.dataset.pmWired = "1";
    el.addEventListener("click", (e) => {
      e.preventDefault();
      PriceModal.open();
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireTriggers);
} else {
  wireTriggers();
}

export default PriceModal;
